export const CHUNK_RELOAD_FINGERPRINTS_KEY = 'forge-chunk-reload';

function safeSessionGet(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionSet(key: string, value: string): boolean {
  try {
    window.sessionStorage.setItem(key, value);
    return true;
  } catch {
    // Session storage unavailable (private mode etc.); reload-once degrades to
    // rethrowing, which the route error boundary handles.
    return false;
  }
}

function isSessionStorageAvailable(): boolean {
  const probeKey = '__forge_session_storage_probe__';
  try {
    window.sessionStorage.setItem(probeKey, '1');
    window.sessionStorage.removeItem(probeKey);
    return true;
  } catch {
    return false;
  }
}

function chunkFailureFingerprint(error: unknown): string {
  const description =
    error instanceof Error ? `${error.name}:${error.message}` : `${typeof error}:${String(error)}`;
  let hash = 2166136261;
  for (let index = 0; index < description.length; index += 1) {
    hash ^= description.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `v1:${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

function isChunkLoadError(error: unknown): error is Error {
  return (
    error instanceof Error &&
    (error.name === 'ChunkLoadError' || /Loading (?:CSS )?chunk [\w-]+ failed/i.test(error.message))
  );
}

function recordedFingerprints(): string[] {
  const raw = safeSessionGet(CHUNK_RELOAD_FINGERPRINTS_KEY);
  return raw ? raw.split(',') : [];
}

/**
 * Loads a lazy route module, recovering from the common deploy failure mode:
 * a new release replaces the hashed chunk files an already-open session's
 * index.html points at, so the dynamic import rejects. Reloading picks up the
 * fresh manifest. A sessionStorage fingerprint list limits each distinct
 * failure to one reload per session — if a chunk still fails after its reload
 * (genuine outage), the error propagates to the router instead of
 * reload-looping, even when other chunks failed and reloaded in between.
 */
export async function loadRouteChunk<M>(importModule: () => Promise<M>): Promise<M> {
  try {
    return await importModule();
  } catch (error) {
    if (!isChunkLoadError(error)) {
      throw error;
    }
    const fingerprint = chunkFailureFingerprint(error);
    const seenFingerprints = recordedFingerprints();
    if (!isSessionStorageAvailable() || seenFingerprints.includes(fingerprint)) {
      throw error;
    }
    if (
      !safeSessionSet(CHUNK_RELOAD_FINGERPRINTS_KEY, [...seenFingerprints, fingerprint].join(','))
    ) {
      throw error;
    }
    window.location.reload();
    // The page is navigating away; never settle so the router doesn't render.
    return new Promise<never>(() => {});
  }
}
